import { checkWorker } from "../../ActionUtils";
import { ActivityOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface SetCurrentActivityOptions {
    activityUpdateOptions?: ActivityOptions;
}




































export class SetCurrentActivity implements Action<Promise<void>> {
    


    readonly #activitySid: string;

    


    readonly #options?: SetCurrentActivityOptions;

    constructor(activitySid: string, options?: SetCurrentActivityOptions) {
        this.#activitySid = activitySid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const activityUpdateOptions = this.#options?.activityUpdateOptions;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        logger.debug("setCurrentActivity called with activitySid: ", this.#activitySid);

        checkWorker(worker, {
            actionName: "SetCurrentActivity",
            moduleName: "actions/Worker",
            logger,
            analytics,
            context,
            event: EVENTS.SetCurrentActivityCompleted,
            extra: {
                activitySid: this.#activitySid,
                rejectPendingReservations: activityUpdateOptions?.rejectPendingReservations
            },
            worker
        });

        const activity = worker.activities.get(this.#activitySid);
        const workerActivity = worker.activity;

        if (this.#activitySid === workerActivity?.sid) {
            const warnMsg = `SetCurrentActivity: Activity ${this.#activitySid} is already set`;
            logger.warn(warnMsg);
            sendWorkerTrackingEvent(EVENTS.SetCurrentActivityCompleted, analytics, worker, {
                warning: warnMsg,
                activitySid: this.#activitySid,
                rejectPendingReservations: activityUpdateOptions?.rejectPendingReservations
            });
            return Promise.resolve();
        }

        try {
            await activity?.setAsCurrent(activityUpdateOptions);
            sendWorkerTrackingEvent(EVENTS.SetCurrentActivityCompleted, analytics, worker, {
                activitySid: this.#activitySid,
                rejectPendingReservations: activityUpdateOptions?.rejectPendingReservations
            });
            return Promise.resolve();
        } catch (error: unknown) {
            const errorMessage = toCauseMessage(error);
            let errorMsg: string;
            let errorCode: ErrorCode;

            const activityUpdatePattern =
                /Worker WK.* cannot have its activity updated while it has .* pending reservations/;
            if (errorMessage && activityUpdatePattern.exec(errorMessage)) {
                errorMsg = `SetCurrentActivity: Cannot set activity to ${this.#activitySid} while current worker ${
                    worker?.sid
                } has other pending reservations`;
                errorCode = ErrorCode.FailedToSetCurrentWorkerActivityDueToPendingReservations;
            } else {
                errorMsg = `SetCurrentActivity: Failed to set activity to ${this.#activitySid} for current worker ${
                    worker?.sid
                }`;
                errorCode = ErrorCode.FailedToSetCurrentWorkerActivity;
            }
            sendWorkerTrackingEvent(EVENTS.SetCurrentActivityCompleted, analytics, worker, {
                error: errorMsg,
                activitySid: this.#activitySid,
                rejectPendingReservations: activityUpdateOptions?.rejectPendingReservations
            });
            logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(context)(errorCode, { severity: ErrorSeverity.Error }, errorMsg, error)
            );
        }
    }
}
